import { List } from 'immutable';
import { TAB_KEY, ASSET_TYPE, TYPE_TAB_MAP, transferTypeMap } from 'constants/assetTransfer';

/**
 * update tab data with included asset links
 * @param {String} tabKey tab key
 * @param {Object} tab tab data on js object format
 * @param {Array} links links
 * @returns updated tab in immutable format
 */
export const updateIncludedAssets = (tabKey, tab, links) => {
    let tabData = tab;
    const dependentIds = links.map(element => element.id);

    tabData = {
        ...tabData,
        links,
        dependenciesLocked: true,
        selectedAssets: [...new Set([...tabData.selectedAssets, ...dependentIds])],
    };

    if (tabData.isObsolete) {
        return { ...tabData, data: new List() };
    }

    const sourceMap = links.reduce((map, element) => {
        map[element.id] = element.parents;
        return map;
    }, {});

    const data = tabData.data.map(element => {
        const id = element.id;

        if (dependentIds.includes(id)) {
            return {
                ...element,
                isSelected: true,
                source: [...new Set([...element.source, ...sourceMap[id]])],
            }
        };
        return element;
    });

    return {
        ...tabData,
        isAllSelected: data.every(item => item.isSelected),
        data: new List(data),
    };
};

/**
 * remove dependencies from immediate child
 * @param {Array} parentIds parent tab removable ids list 
 * @param {Object} tabData tab data
 * @returns {Object} child ids of empty sources with updated tab data
 */
export const removeDependencies = (parentIds, tabData) => {
    const removed = [];

    const updatedLinks = tabData.links
        .map(item => {
            const updatedParents = item.parents.filter(id => !parentIds.includes(id));
            if (tabData.isObsolete && item.parents.length && !updatedParents.length) {
                removed.push(item.id);
            }
            return {
                ...item,
                parents: updatedParents,
            }
        })
        .filter(item => !!item.parents.length);

    if (tabData.isObsolete) {
        return {
            tabData: {
                links: updatedLinks,
                data: new List(),
                selectedAssets: updatedLinks.map(item => item.id),
                dependenciesLocked: true,
                isObsolete: true,
            },
            removed,
        }
    }

    const updatedData = tabData.data.map(item => {
        const id = item.id;
        const updateSource = item.source.filter(id => !parentIds.includes(id))

        if (item.source.length && !updateSource.length) {
            removed.push(id);
        };
        return {
            ...item,
            isSelected: !!updateSource.length,
            source: updateSource,
        };
    });
    const updatedSelectedAssets = updatedData.filter(item => item.isSelected).map(item => item.id);

    return {
        tabData: {
            links: updatedLinks,
            data: new List(updatedData),
            selectedAssets: updatedSelectedAssets,
            dependenciesLocked: true,
            isObsolete: false,
        },
        removed,
    };
}

/**
 * generate selected assets
 * @param {String} resource resource type
 * @param {SelectiveTransferTab} tab tab data
 * @returns {Object} assets
 */
const deriveAssetEntity = (resource, tab) => {
    let allSelected = false;
    let assets;

    if (resource === ASSET_TYPE.RECIPE_CATEGORY) {
        assets = tab.data
            .filter(item => item.isSelected)
            .map(item => item.userCategoryId);
    } else {
        allSelected = tab.isAllSelected;
        assets = allSelected ? [] : tab.selectedAssets;
    }

    return {
        allSelected,
        ...assets && assets.length && { assets: [...new Set(assets)] },
    }
}

/**
 * Create asset transfer request body by state
 * @param {SelectiveTransfer} transferState selective transfer state
 * @param {Number} initiatedBy initiated by
 * @returns {Object} asset transfer request body
 */
export const generateAssetTransferEntity = (transferState, initiatedBy) => {
    const destination = transferState.get('destinations').map(account => account.get('accountId')).toArray();
    const assets = {};
    const transferNote = transferState.get('transferNote');
    let data;

    for (const type of Object.values(ASSET_TYPE)) {
        let tabData = transferState.get(TYPE_TAB_MAP[type]);

        if (type === ASSET_TYPE.RECIPE) {
            const batchRecipes = transferState.get(TAB_KEY.BATCH_RECIPE);
            tabData = tabData.merge({
                isAllSelected: tabData.get('isAllSelected') && batchRecipes.get('isAllSelected'),
                selectedAssets: [...tabData.get('selectedAssets'), ...batchRecipes.get('selectedAssets')],
            });
        }

        data = deriveAssetEntity(type, tabData.toJS());

        if (data.allSelected || (data.assets && data.assets.length)) {
            assets[type] = data;
        }
    }

    return {
        source: transferState.getIn(['source', 'accountId']),
        destination,
        initiatedBy,
        assets,
        transferType: transferTypeMap.copy,
        ...transferNote && { transferNote },
    };
}
